import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "react-hot-toast";
import { FiLogOut, FiLock, FiX } from "react-icons/fi";
import { MdOutlineLightMode, MdOutlineDarkMode } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { resetPassword } from "../../redux/actions/profile/resetPasswordActions";

export default function Navbar({ darkMode, setDarkMode }) {
  const [role, setRole] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector(
    (state) => state.resetPassword || { loading: false },
  );

  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    if (storedRole) {
      setRole(storedRole);
    }
  }, []);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      const result = await dispatch(resetPassword(passwordData));
      if (result?.meta?.status) {
        setShowResetModal(false);
        setPasswordData({ newPassword: "", confirmPassword: "" });
      }
    } catch (error) {
      // Error handled in action
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-900 h-16 px-6 flex items-center border-b border-gray-200 dark:border-gray-800 justify-end w-full shadow-sm">
        {/* Right Side Controls */}
        <div className="flex items-center gap-4">
          {/* User Profile */}
          <Menu as="div" className="relative">
            <div className="flex items-center gap-3">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {role || "Guest"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Administrator
                </p>
              </div>
              <Menu.Button className="flex items-center gap-2 hover:ring-2 hover:ring-orange-500 rounded-full p-0.5 transition-all duration-200 focus:outline-none">
                <div
                  className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 border-2 border-white dark:border-gray-800 bg-cover bg-no-repeat bg-center shadow-md hover:shadow-lg transition-all hover:scale-105"
                  style={{
                    backgroundImage:
                      'url("https://i.pinimg.com/474x/67/c3/d6/67c3d63e215e034e01d45c8256d720d3.jpg")',
                  }}
                />
              </Menu.Button>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700 rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                <div className="px-4 py-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Signed in as
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {role || "Guest"}
                  </p>
                </div>

                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active ? "bg-gray-50 dark:bg-gray-700" : ""
                        } flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 transition-colors`}
                        onClick={() => setShowResetModal(true)}
                      >
                        <FiLock className="w-4 h-4 text-gray-400" />
                        Reset Password
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active ? "bg-gray-50 dark:bg-gray-700" : ""
                        } flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 transition-colors`}
                        onClick={() => setDarkMode(!darkMode)}
                      >
                        {darkMode ? (
                          <MdOutlineLightMode className="w-4 h-4 text-gray-400" />
                        ) : (
                          <MdOutlineDarkMode className="w-4 h-4 text-gray-400" />
                        )}
                        {darkMode ? "Light Mode" : "Dark Mode"}
                      </button>
                    )}
                  </Menu.Item>
                </div>

                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active ? "bg-red-50 dark:bg-red-900/20" : ""
                        } flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 transition-colors`}
                        onClick={() => {
                          localStorage.clear();
                          window.location.href = "/auth";
                        }}
                      >
                        <FiLogOut className="w-4 h-4" />
                        Logout
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Reset Password
              </h3>
              <button
                onClick={() => {
                  setShowResetModal(false);
                  setPasswordData({ newPassword: "", confirmPassword: "" });
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleResetSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter new password"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm Password *
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Confirm new password"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-600 dark:text-gray-400">
                  Show passwords
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                    setPasswordData({ newPassword: "", confirmPassword: "" });
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
